import { Platform } from "react-native";
import { apiRequest } from "./api";

// Lazy-require so OTA bundles targeting older runtimes that lack the native
// modules (1.4.x builds without expo-notifications baked in) don't crash on
// import. On those builds these helpers gracefully no-op.
let Notifications: any = null;
let Device: any = null;
let Constants: any = null;
let NATIVE_READY = false;
if (Platform.OS !== "web") {
  try {
    Notifications = require("expo-notifications");
    Device = require("expo-device");
    Constants = require("expo-constants").default;
    NATIVE_READY = true;
  } catch {
    NATIVE_READY = false;
  }
}

if (Notifications) {
  // Configure how foreground notifications show (banner + sound). Without this,
  // notifications arrive silently when the app is open.
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    }),
  });
}

/**
 * Request notification permission AND register the device's Expo push token
 * with our server. Idempotent. No-op on web, simulators, or older app builds
 * that don't have expo-notifications baked in.
 *
 * Returns true if we now have a token registered server-side (or already did),
 * false if permission was denied or anything failed.
 */
export async function registerForPushNotifications(): Promise<boolean> {
  if (!NATIVE_READY) return false;
  if (!Device?.isDevice) return false; // Simulators can't get a push token

  try {
    // Step 1 — permission. Don't ask if already granted/denied — system handles
    // the prompt only once per install.
    let { status } = await Notifications.getPermissionsAsync();
    if (status !== "granted") {
      const ask = await Notifications.requestPermissionsAsync();
      status = ask.status;
    }
    if (status !== "granted") return false;

    // Android: a default channel is required for notifications to display.
    if (Platform.OS === "android") {
      await Notifications.setNotificationChannelAsync("default", {
        name: "Dash-Hound",
        importance: Notifications.AndroidImportance.HIGH,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: "#F5A623",
      });
    }

    // Step 2 — token. Requires projectId on newer SDKs.
    const projectId = Constants?.expoConfig?.extra?.eas?.projectId
      ?? Constants?.easConfig?.projectId;
    const tokenResp = await Notifications.getExpoPushTokenAsync(
      projectId ? { projectId } : undefined,
    );
    const token = tokenResp?.data;
    if (!token) return false;

    // Step 3 — server registration. Best-effort: if it fails, the user can
    // retry next launch / next Follow. Don't bubble the error.
    try {
      await apiRequest("/push-tokens", {
        method: "POST",
        body: JSON.stringify({ token, platform: Platform.OS as "ios" | "android" }),
      });
      return true;
    } catch {
      return false;
    }
  } catch {
    return false;
  }
}

/**
 * Returns the current permission status without prompting. Used to show
 * the right copy on the settings toggle (e.g. "Open Settings to enable").
 */
export async function getNotificationPermissionStatus(): Promise<"granted" | "denied" | "undetermined"> {
  if (!NATIVE_READY || !Device?.isDevice) return "denied";
  try {
    const { status } = await Notifications.getPermissionsAsync();
    return status as any;
  } catch {
    return "undetermined";
  }
}
